import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "antd";
import { useSelector } from "react-redux";
import { jsPDF } from 'jspdf'
import { applyPlugin } from 'jspdf-autotable'

import Logo from "../../assets/logo.png";

import TableHeader from "../../components/Common/TableHeader";
import InfoCard from "../../components/Common/InfoCard";
import Table from "../../components/Common/Table";
import NotFound from "../../components/NotFound";
import Loader from "../../components/Common/Loader";

import { formatDD_MM_YYYY_HH_MM, formatDateToISO } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import { useThunk } from "../../hooks/useThunk";
import { fetchUserProperties } from "../../store";
import { fetchPaymentHistory, downloadPaymentHistory } from "../../store/thunks/paymentHistoryThunk";
import showToast from "../../utils/toastNotifications";

const isUnitIdFormat = (searchTerm) => {
  if (!searchTerm) return false;
  // Check if the search term matches the pattern: UNIT-{number}
  const unitIdPattern = /^UNIT-\d+$/i;
  return unitIdPattern.test(searchTerm.trim());
};

const getUniqueTransactions = (data) => {
  const map = new Map();
  data.forEach(item => {
    const key = item.transactionId;
    if (!map.has(key)) {
      map.set(key, item);
    }
  });
  return Array.from(map.values());
}

const PaymentHistory = () => {
  const ITEMS_PER_PAGE = 10;
  
  const [doFetchPaymentHistory, isLoading, fetchError] = useThunk(fetchPaymentHistory);
  const [doFetchUserProperties, isFetchUserProperties, fetchUserPropertiesError] = useThunk(fetchUserProperties);
  const [doDownloadPaymentHistory, isDownloading, downloadError] = useThunk(downloadPaymentHistory);

  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get("page")) || 1;
  const search = searchParams.get("search") || "";
  const propertyId = searchParams.get("propertyId") || "";
  const startDateParam = searchParams.get("startDate") || "";
  const endDateParam = searchParams.get("endDate") || "";

  const [searchValue, setSearchValue] = useState(search);
  const [propertyFilter, setPropertyFilter] = useState(propertyId);
  const [filters, setFilters] = useState({
    startDate: startDateParam,
    endDate: endDateParam
  });
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);

  const [historyData, setHistoryData] = useState([]);
  const [metrics, setMetrics] = useState([]);
  const [pagination, setPagination] = useState({});
  const [properties, setProperties] = useState([]);
    
  const loadUserProperties = async () => {
    const result = await doFetchUserProperties();
    if (result?.success) {
    } else {
      console.error("Error fetching properties:", result?.error?.message);
    }
  }

  const fetchData = useCallback(async (download = false, {startDate, endDate} = {}) => {
    const startDateISO = formatDateToISO(startDate || filters.startDate, false);
    const endDateISO = formatDateToISO(endDate || filters.endDate, true);

    // Determine if search term is a unit ID or tenant name
    const isUnitSearch = isUnitIdFormat(search);
    
    const params = {
      offset: download ? null : (currentPage - 1) * ITEMS_PER_PAGE,
      limit: download ? null : ITEMS_PER_PAGE, 
      TenantName: isUnitSearch ? null : search,
      unitId: isUnitSearch ? search : null, 
      propertyMongoId: propertyFilter && propertyFilter !== "undefined" ? propertyFilter : null,
      startDate: startDateISO, // Start of day
      endDate: endDateISO,     // End of day
    };

    // console.log("Final params:", params);
    const result = await doFetchPaymentHistory(params);
    const data = result?.response?.data || {}

    if (result.success) {
      if (!download) {
        setHistoryData(data?.data || []);
        setMetrics(data?.metrics || []);
        setPagination(data?.pagination || {});
        setProperties(data?.properties || []);
      }
      return result?.response;

    } else {
      showToast("error",  result?.error?.message || "An error occurred while fetching payment history.");
    }
  }, [filters, propertyFilter, searchParams, doFetchPaymentHistory]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleSearch = () => {
    const selectedProperty = properties.find(p => p._id === propertyFilter);
    const propertyName = selectedProperty ? selectedProperty.propertyName : "";
    const newParams = new URLSearchParams();
    newParams.set("search", searchValue);
    newParams.set("propertyName", propertyName);
    if (filters.startDate) newParams.set("startDate", filters.startDate);
    if (filters.endDate) newParams.set("endDate", filters.endDate);
    newParams.set("page", 1);

    setSearchParams(newParams);
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set("page", page);
    setSearchParams(newParams);
  };

  const handleDateChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const columns = [
    {
      title: "Date / Time",
      dataIndex: "paymentDate",
      key: "paymentDate",
      render: (val) => formatDD_MM_YYYY_HH_MM(val),
    },
    { title: "Tenant Name", dataIndex: "tenantName", key: "tenantName" },
    {
      title: "Unit / Property",
      dataIndex: "unitProperty",
      key: "unitProperty",
      render: (_, row) => `${row.unitId || "-"} / ${row.propertyName || "-"}`,
    },
    {
      title: "Amount Paid (Tenant)",
      dataIndex: "amount",
      key: "amount",
      render: (val) => `UGX ${val?.toFixed(2)}`,
    },
    {
      title: "Amount to Mobile Money",
      dataIndex: "amountToLandlord",
      key: "amountToLandlord",
      render: (val) => `UGX ${val?.toFixed(2)}`,
    },
    {
      title: "Reference / Channel",
      dataIndex: "transactionId",
      key: "transactionId",
    },
  ];

  const transformData = (data, prefix = "item") =>
    (data || []).map((item, i) => ({
      ...item,
      key: `${prefix}-${i}`,
  }));

  const transformedData = useMemo(() => transformData(historyData, "pay"), [historyData]);

  const propertyOptions = useMemo(() => {
    // Filter properties to get only id and name, then create options
    const options = [
      { label: "All properties", value: "" },
      ...properties
        .filter(property => property.propertyId && property.propertyName) // Filter out invalid properties
        .map(property => ({
          label: property.propertyName,
          value: property.propertyId,
        })),
    ];
    return options;
  }, [properties]);

  // Function to get property name from property ID
  const getPropertyNameById = useCallback((propertyId) => {
    if (!propertyId) return null;
    const property = propertyOptions.find(option => option.value === propertyId);
    return property ? property.label : null; 
  }, [propertyOptions]);

  const createHeaderSVG = (amount) => {
    
    const formattedAmount = amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

    const amountLength = formattedAmount.length;

    let amountXPosition = 160;
    let amountWidth = 140;
    
    if (amountLength > 15) {
      // Very large amounts (e.g., "12,345,678,901.23" - 16+ chars)
      amountXPosition = 40;   // Move further left
      amountWidth = 280;      // Much wider card
    } else if (amountLength > 12) {
      // Large amounts (e.g., "123,456,789.12" - 13-15 chars)
      amountXPosition = 80;   // Move left
      amountWidth = 240;      // Wider card
    } else if (amountLength > 9) {
      // Medium amounts (e.g., "1,234,567.89" - 10-12 chars)
      amountXPosition = 120;   // Slightly left
      amountWidth = 200;      // Moderate width
    } else if (amountLength > 6) {
      // Small-medium amounts (e.g., "12,345.67" - 7-9 chars)
      amountXPosition = 140;   // Your current setting
      amountWidth = 180;      // Slightly larger than current
    } else {
      // Small amounts (e.g., "123.45" - ≤6 chars)
      amountXPosition = 160;  // More centered
      amountWidth = 160;      // Compact card
    }

    const textAnchor = amountXPosition + (amountWidth / 2);

    const svgContent = `
      <svg width="800" height="200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <filter id="cardShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="2" flood-color="rgba(45, 63, 115, 0.15)"/>
          </filter>
          <filter id="containerShadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="0" dy="4" stdDeviation="8" flood-color="rgba(0,0,0,0.06)"/>
          </filter>
          <!-- Fixed background color -->
          <pattern id="cardBg" patternUnits="userSpaceOnUse" width="1" height="1">
            <rect width="1" height="1" fill="#9FB6FB" opacity="0.1"/>
          </pattern>
        </defs>
        
        <!-- Main Container Background -->
        <rect x="5" y="5" width="790" height="120" rx="10" fill="white" filter="url(#containerShadow)"/>
        
        <!-- Left Side  -->
        <text x="25" y="70" font-family="Arial, sans-serif" font-size="18" font-weight="600" fill="#4772F1">
          ${propertyFilter && propertyFilter !== "undefined" ? getPropertyNameById(propertyFilter) : 'All Properties'}
        </text>
        
        <!-- Right Side - Rent Collected card -->
        <g transform="translate(450, 20)">
          <!-- Card -->
          <rect x="${amountXPosition}" y="0" width="${amountWidth}" height="80" rx="5" fill="url(#cardBg)" filter="url(#cardShadow)" stroke="#f0f0f0" stroke-width="1"/>
          <text x="${textAnchor}" y="30" font-family="Arial, sans-serif" font-size="12" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            Rent Collected (UGX)
          </text>
          <text x="${textAnchor}" y="55" font-family="Arial, sans-serif" font-size="${amountLength > 12 ? '18' : '22'}" font-weight="600" text-anchor="middle" fill="url(#textGradient)">
            ${formattedAmount}
          </text>
        </g>
        
        <!-- Divider Line -->
        <line x1="440" y1="30" x2="440" y2="170" stroke="#f0f0f0" stroke-width="1" opacity="0.5"/>
      </svg>
    `;
    
    return svgContent;
  };

  const handleDownloadPDFWithJspdf = async () => {
    try {

      // Check if date filters are applied
      const hasDateFilters = filters.startDate && filters.endDate;
      
      let dateRangeText = '';
      let startDate = hasDateFilters ? new Date(filters.startDate) : null;
      let endDate = hasDateFilters ? new Date(filters.endDate) :  null;

      if (!hasDateFilters) {
        // Apply 6-month limit when no date range is selected
        const today = new Date();
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(today.getMonth() - 6);
        
        dateRangeText = `${sixMonthsAgo.toLocaleDateString()} to ${today.toLocaleDateString()}`;
        startDate = sixMonthsAgo;
        endDate = today;

      } else {
        // Check if selected date range is larger than 6 months
        const timeDifference = endDate.getTime() - startDate.getTime();
        const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
        const maxDaysAllowed = 180; // Approximately 6 months
        
        if (daysDifference > maxDaysAllowed) {
          // Limit to 6 months from the end date
          const limitedStartDate = new Date(endDate);
          limitedStartDate.setMonth(limitedStartDate.getMonth() - 6);

          dateRangeText = `${limitedStartDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`;
          startDate = limitedStartDate;

          // Show warning to user
          showToast("warning", `Selected date range exceeds 6 months. PDF limited to 6 months from ${endDate.toLocaleDateString()} backwards.`);
          
        } else {
          // Use the selected date range as is
          const startDateFormatted = formatDateToISO(filters.startDate, false).split('.')[0];
          const endDateFormatted = formatDateToISO(filters.endDate, true).split('.')[0];
          dateRangeText = `${startDateFormatted} to ${endDateFormatted}`;
        }
      }

      const { data } = await fetchData(true, { startDate, endDate });

      const filteredData = transformData(data?.data, "pay");

      if (filteredData.length === 0) {
        showToast("warning", "No data available for the selected date range.");
        setIsPdfGenerating(false);
        return;
      }
      // Add progress indication for large datasets
      if (filteredData.length > 500) {
        showToast("info", "Generating PDF for large dataset. Please wait...");
      }

      // Create new PDF document
      applyPlugin(jsPDF);
      const pdf = new jsPDF('p', 'mm', 'a4');

      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;

      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

      yPosition += 20;

      // report title
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Payment History Report', 20, yPosition);

      yPosition += 10;

      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);

      pdf.setTextColor(0, 0, 0);
      pdf.text(dateRangeText, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Property:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const selectedPropertyName = propertyFilter && propertyFilter !== "undefined" ? getPropertyNameById(propertyFilter) : 'All Properties';
      pdf.text(selectedPropertyName, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;

      // applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      if (search) {
        pdf.text(`• Search Term: ${search}`, 25, yPosition);
        yPosition += 5;
      }
      
      if (selectedPropertyName) {
        pdf.text(`• Property Filter: ${selectedPropertyName}`, 25, yPosition);
        yPosition += 5;
      }
      
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 5;
      
      // Add total amount info box
      const paymentInfoCard = createHeaderSVG(data?.metrics?.totalRentCollected);

        const paymentInfoCardCanvas = await svgToCanvas(paymentInfoCard, 800, 200);
        const paymentInfoCardImgData = paymentInfoCardCanvas.toDataURL('image/png');
        const paymentInfoCardWidth = 170; // Full width within margins
        const paymentInfoCardHeight = (200 * paymentInfoCardWidth) / 800; // Proportional height

        pdf.addImage(paymentInfoCardImgData, 'PNG', 20, yPosition, paymentInfoCardWidth, paymentInfoCardHeight);
        yPosition += paymentInfoCardHeight;
      
      // Prepare table data
      const tableData = filteredData.map(item => [
        formatDD_MM_YYYY_HH_MM(item.paymentDate),
        item.tenantName,
        `${item.unitId} / ${item.propertyName}`,
        `UGX ${item.amount?.toFixed(2)}`,
        `UGX ${item.amountToLandlord?.toFixed(2)}`,
        item.transactionId
      ]);
      
      // Define table headers
      const tableHeaders = [
        'Date / Time',
        'Tenant Name',
        'Unit / Property',
        'Amount Paid (Tenant)',
        'Amount to Mobile Money',
        'Reference / Channel'
      ];
      
      // Add the table
      pdf.autoTable({
        head: [tableHeaders],
        body: tableData,
        startY: yPosition,
        theme: 'grid',
        headStyles: {
          fillColor: [71, 114, 241], //rgba(71, 114, 241, 1);
          textColor: [255, 255, 255],
          fontSize: 9,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 8,
          textColor: [0, 0, 0],
          cellPadding: 3
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250] // Light gray for alternate rows
        },
        columnStyles: {
          0: { cellWidth: 28, halign: 'center' }, // Date
          1: { cellWidth: 25, halign: 'left' },   // Tenant Name
          2: { cellWidth: 32, halign: 'left' },   // Unit/Property
          3: { cellWidth: 28, halign: 'right' },  // Amount Paid
          4: { cellWidth: 28, halign: 'right' },  // Amount to MM
          5: { halign: 'center' }  // Reference
        },
        margin: { left: 20, right: 20, top: 20, bottom: 20 },
        tableLineColor: [200, 200, 200],
        tableLineWidth: 0.1,
        styles: {
          overflow: 'linebreak',
          fontSize: 8
        }
      });
      
      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        
        const footerY = pageHeight - 15;

        // header section
        pdf.setFontSize(10);
        pdf.setTextColor(71, 114, 241); // Blue color for brand
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, 10);
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('Real Estate Management Software', 20, 14);
        
        pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);
        
        // Footer section
        pdf.setFontSize(8);
        pdf.setTextColor(71, 114, 241);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, footerY + 3);
        
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('|', 42, footerY + 3);
        pdf.text('www.tenafix.co', 44, footerY + 3);
        pdf.text('|', 64, footerY + 3);
        pdf.text('support@tenafix.co', 66, footerY + 3);
        
        // System generated note
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('This is a system-generated report.', 20, footerY + 6);
        pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
        
        // Page number (right aligned)
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }
      
      // Generate filename with current date
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      const fileName = `payment-history-report-${currentDate}.pdf`;
      
      // Save the PDF
      pdf.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast("error", "Failed to generate PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleDownloadClick = () => {
    setIsPdfGenerating(true);
    setTimeout(() => handleDownloadPDFWithJspdf(), 0);
  };

  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <div className="">
        <TableHeader
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          onSearchSubmit={handleSearch}
          searchPlaceholder="Tenant name / Unit ID"
          filters={[
            {
              name: "Filter by Property",
              type: "dropdown",
              value: getPropertyNameById(propertyFilter),
              onChange: (val) => setPropertyFilter(val),
              options: propertyOptions,
            },
          ]}
          datePicker={{
            startDate: filters.startDate,
            endDate: filters.endDate,
            onChange: handleDateChange,
          }}
          buttons={[
            {
              label: "SEARCH",
              onClick: handleSearch,
              variant: "main",
              loading: isLoading,
              className:
              "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[100px] uppercase font-medium flex items-center justify-center",
            },
            {
              label: "DOWNLOAD REPORT",
              onClick: handleDownloadClick,
              variant: "secondary",
              loading: isPdfGenerating || isLoading,
              disabled: isPdfGenerating || isLoading,
              className:
                "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] font-bold uppercase w-full sm:w-[138px] md:w-[174px] lg:w-[154px] [@media(min-width:1080px)]:w-[184px] [@media(min-width:1708px)]:w-[247px] ",
            },
          ]}
          containerClass="md:grid sm:grid-cols-2 md:grid-cols-3 [@media(min-width:1214px)]:grid-cols-4 flex flex-wrap "
          datePickerClass="md:w-auto flex w-full"
          buttonWrapperClass="md:mr-0 w-full xs:mx-auto xs:w-auto sm:w-full sm:flex lg sm:justify-start [@media(min-width:1214px)]:justify-end"
        />
      </div>

      <div className="mb-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F] mt-[30px]">
        <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center sm:gap-8">
          <div className="text-[#4772F1] text-[22px] font-semibold font-['Inter'] leading-none flex items-center pl-3 whitespace-nowrap">
            {propertyFilter && propertyFilter !== "undefined" ? getPropertyNameById(propertyFilter) : 'All Properties'}
          </div>
          <div>
            <InfoCard
              label="Rent Collected (UGX)"
              value={metrics?.totalRentCollected?.toFixed(2)}
              className=" max-h-[120px] mx-auto justify-end"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>

      <div className="">
        {isLoading && !isPdfGenerating ? (
          <div
            className="font-inter text-[15px] text-[rgb(112,112,112)] flex flex-col gap-[10px] items-center justify-center py-9 w-full bg-white rounded-[10px]"
            style={{ boxShadow: "0px 4px 4px 0px #0000000F" }}
          >
            <Loader color="#4772F1" />
          </div>
        ) : fetchError ? (
          <NotFound
            text1="Error fetching payment history"
            text2={
              fetchError.message || "An error occurred while fetching data."
            }
            isButton={false}
          />
        ) : transformedData.length === 0 ? (
          <NotFound
            text1="No payments found"
            text2="No payments match your current filter criteria."
            isButton={false}
          />
        ) : (
          <>
            <div>
              <Table
                dataSource={transformedData}
                columns={columns}
                hasSubTable={false}
                rowKey="key"
              />
              <div className="flex justify-center mt-4">
                <Pagination
                  current={currentPage}
                  total={pagination?.totalDocuments}
                  pageSize={ITEMS_PER_PAGE}
                  onChange={handlePageChange}
                  showSizeChanger={false}
                  hideOnSinglePage
                  className="custom-pagination"
                  showLessItems
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentHistory;
